if (!customElements.get('related-products-slider')) {
  customElements.define(
    'related-products-slider',
    class RelatedProductsSlider extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this.isDown = false;
        this.startX = 0;
        this.scrollStart = 0;
        this.dragThreshold = 6;
        this.hasDragged = false;
        this.onScrollHandler = this.onScroll.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.slider = this.querySelector('.related-products__slider');
        this.prevBtn = this.querySelector('.related-products__nav-btn--prev');
        this.nextBtn = this.querySelector('.related-products__nav-btn--next');

        if (!this.slider) return;

        const slides = this.slider.querySelectorAll('.related-products__slide');
        if (slides.length <= 1) {
          if (this.prevBtn) this.prevBtn.style.display = 'none';
          if (this.nextBtn) this.nextBtn.style.display = 'none';
          return;
        }

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.scrollStep('prev');
          });
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.scrollStep('next');
          });
        }

        this.slider.addEventListener('scroll', this.onScrollHandler, { passive: true });

        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateNavButtons();
          });
          this.resizeObserver.observe(this.slider);
        }

        this.initDragToScroll();
        this.updateNavButtons();
      }

      disconnectedCallback() {
        if (this.slider) {
          this.slider.removeEventListener('scroll', this.onScrollHandler);
          if (this.onMouseDownHandler) this.slider.removeEventListener('mousedown', this.onMouseDownHandler);
          if (this.onMouseMoveHandler) this.slider.removeEventListener('mousemove', this.onMouseMoveHandler);
          if (this.onClickCaptureHandler) this.slider.removeEventListener('click', this.onClickCaptureHandler, true);
        }
        if (this.onMouseUpHandler) {
          window.removeEventListener('mouseup', this.onMouseUpHandler);
          window.removeEventListener('mouseleave', this.onMouseUpHandler);
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      getSlideStride() {
        const slides = this.slider.querySelectorAll('.related-products__slide');
        if (slides.length < 2) return this.slider.clientWidth * 0.75;
        const rect0 = slides[0].getBoundingClientRect();
        const rect1 = slides[1].getBoundingClientRect();
        const stride = rect1.left - rect0.left;
        if (stride > 0) return stride;
        const style = window.getComputedStyle(this.slider);
        const gap = parseFloat(style.columnGap || style.gap || 20);
        return slides[0].offsetWidth + gap;
      }

      scrollStep(direction) {
        if (!this.slider) return;
        const stride = this.getSlideStride();
        const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
        const currentScroll = this.slider.scrollLeft;

        let targetScroll;
        if (direction === 'next') {
          targetScroll = Math.min(maxScroll, currentScroll + stride);
        } else {
          targetScroll = Math.max(0, currentScroll - stride);
        }

        this.slider.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      }

      onScroll() {
        this.updateNavButtons();
      }

      updateNavButtons() {
        if (!this.slider) return;
        const sl = this.slider.scrollLeft;
        const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);

        const isAtStart = sl <= 6;
        const isAtEnd = sl >= maxScroll - 6;

        if (this.prevBtn) {
          if (isAtStart) {
            this.prevBtn.setAttribute('data-disabled', 'true');
            this.prevBtn.setAttribute('aria-disabled', 'true');
            this.prevBtn.setAttribute('tabindex', '-1');
          } else {
            this.prevBtn.removeAttribute('data-disabled');
            this.prevBtn.removeAttribute('aria-disabled');
            this.prevBtn.removeAttribute('tabindex');
          }
        }

        if (this.nextBtn) {
          if (isAtEnd || maxScroll <= 2) {
            this.nextBtn.setAttribute('data-disabled', 'true');
            this.nextBtn.setAttribute('aria-disabled', 'true');
            this.nextBtn.setAttribute('tabindex', '-1');
          } else {
            this.nextBtn.removeAttribute('data-disabled');
            this.nextBtn.removeAttribute('aria-disabled');
            this.nextBtn.removeAttribute('tabindex');
          }
        }
      }

      initDragToScroll() {
        const slider = this.slider;
        if (!slider) return;

        this.onMouseDownHandler = (e) => {
          if (e.target.closest('button, input, select, textarea')) return;
          this.isDown = true;
          this.hasDragged = false;
          this.startX = e.pageX - slider.offsetLeft;
          this.scrollStart = slider.scrollLeft;
          slider.classList.add('is-dragging');
        };

        this.onMouseUpHandler = () => {
          if (!this.isDown) return;
          this.isDown = false;
          slider.classList.remove('is-dragging');
        };

        this.onMouseMoveHandler = (e) => {
          if (!this.isDown) return;
          const x = e.pageX - slider.offsetLeft;
          const walk = (x - this.startX) * 1.25;
          if (Math.abs(x - this.startX) > this.dragThreshold) {
            this.hasDragged = true;
          }
          slider.scrollLeft = this.scrollStart - walk;
        };

        this.onClickCaptureHandler = (e) => {
          if (this.hasDragged) {
            e.preventDefault();
            e.stopPropagation();
            this.hasDragged = false;
          }
        };

        slider.addEventListener('mousedown', this.onMouseDownHandler);
        window.addEventListener('mouseup', this.onMouseUpHandler);
        window.addEventListener('mouseleave', this.onMouseUpHandler);
        slider.addEventListener('mousemove', this.onMouseMoveHandler);
        slider.addEventListener('click', this.onClickCaptureHandler, true);
      }
    }
  );
}
